<template>
  <div>
    <div class="box-notification-header">
      <svgicon name="action/assassination_alt" />
      <div
        class="name"
        v-html="$tmd(
          `notification.box.assassination.title`,
          { system: data.system.name }
        )">
      </div>
      <div class="outcome">
        {{ $t(`notification.box.outcome.${data.side}.${data.outcome}`) }}
      </div>
    </div>

    <div class="box-notification-tabs">
      <div class="box-notification-tab-buttons">
        <div
          v-for="(tab, i) in tabs"
          :key="i"
          :class="{ 'active': activeTab === i }"
          @click="activeTab = i"
          class="box-notification-tab-button">
        </div>
      </div>

      <div class="box-notification-tab-item">
        <div
          v-if="tabs[activeTab].includes('text')"
          class="box-notification-bloc"
          v-html="$tmd(
            `notification.box.assassination.description.${data.side}.${data.outcome}`,
            {
              system: data.system.name,
              spy: data.spy.current.name,
              spy_player: isSpyAnonymous ? '' : data.spy.current.owner.name,
              target: data.target.name,
              target_player: data.target.owner.name,
            }
          )">
        </div>
        <div
          v-if="tabs[activeTab].includes('bop')"
          class="box-notification-action toolbox-actions">
          <action-overview :data="overview" />
        </div>
        <div
          v-if="tabs[activeTab].includes('level')"
          class="box-notification-bloc is-boxed">
          <svgicon name="bookmark" />
          <span v-html="$tmd('notification.box.level_gained', { level: data.spy.current.level })"></span>
        </div>
        <div
          v-if="tabs[activeTab].includes('cover')"
          class="box-notification-bloc is-boxed">
          <span v-html="$tmd('notification.box.cover_lost', { cover })"></span>
        </div>
        <div
          v-if="tabs[activeTab].includes('spy')"
          class="box-notification-bloc">
          <h2>{{ $t('notification.box.attacker') }}</h2>
          <character-card
            :character="data.spy.previous"
            :diff="(data.side === 'attacker' ? data.spy.current : null)"
            :theme="theme(data.spy.current.owner.faction)" />
        </div>
        <!-- Stage 8 F3: when the spy stayed undercover, the server emits
             the spy at the anonymous tier (only type+level reach the wire).
             We hide the identifying card and the attacker block above so
             the defender does not learn the attacker's identity from the
             UI either. -->
        <div
          v-if="tabs[activeTab].includes('anonymous-spy')"
          class="box-notification-bloc">
          <h2>{{ $t('notification.box.attacker') }}</h2>
          <p>{{ $t('notification.box.unknown_spy') }}</p>
        </div>
        <div
          v-if="tabs[activeTab].includes('target')"
          class="box-notification-bloc">
          <h2>{{ $t('notification.box.target') }}</h2>
          <character-card
            :character="data.target"
            :theme="theme(data.target.owner.faction)" />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CharacterCard from '@/game/components/card/CharacterCard.vue';
import ActionOverview from '@/game/components/galaxy/system/ActionOverview.vue';

export default {
  name: 'assassination-notif',
  props: {
    data: Object,
  },
  data() {
    return {
      activeTab: 0,
    };
  },
  computed: {
    // Stage 8 F3 — when the server obfuscated the spy down to the
    // anonymous tier (vis=1, undercover branch), the spy struct has
    // no `name`. Surface that to the template so we can render the
    // "unknown enemy spy" placeholder instead of the identifying
    // character card.
    isSpyAnonymous() {
      return !this.data.spy.current || !this.data.spy.current.name;
    },
    tabs() {
      const tab1 = ['text', 'bop'];
      const tab2 = this.isSpyAnonymous ? ['anonymous-spy'] : ['spy'];
      const tab3 = ['target'];

      if (this.data.side === 'attacker') {
        tab1.push('cover');

        if (this.data.spy.current.level > this.data.spy.previous.level) {
          tab1.push('level');
        }
      }

      return [tab1, tab2, tab3];
    },
    overview() {
      // Stage 8 F3 — when the server obfuscated the attacker spy to the
      // anonymous tier (vis=1, undercover defender branch), `previous.owner`
      // is null because :owner is not in the level-1 field list. Fall back to
      // an undefined theme so the bop bar still renders without identifying
      // the attacker faction.
      const previousOwner = this.data.spy.previous && this.data.spy.previous.owner;
      return {
        attacker: this.data.balance_of_power.attack,
        attackerIcon: 'action/assassination_alt',
        attackerModifier: this.data.spy.previous.level,
        attackerTheme: previousOwner ? this.theme(previousOwner.faction) : null,
        defender: this.data.balance_of_power.defense,
        defenderIcon: 'agent/protection',
        defenderTheme: this.theme(this.data.target.owner.faction),
        result: this.data.balance_of_power.result,
      };
    },
    cover() {
      return Math.round(this.data.spy.previous.spy.cover.value - this.data.spy.current.spy.cover.value);
    },
  },
  methods: {
    theme(factionKey) {
      return this.$store.getters['game/themeByKey'](factionKey);
    },
  },
  components: {
    CharacterCard,
    ActionOverview,
  },
};
</script>
